import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { LuShieldCheck } from "react-icons/lu";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import PageHeader from "../../app-components/ui/PageHeader";
import { card } from "../../app-components/ui/styles";
import { SITELINKS } from "../../app-routes/links";
import { getErrorMessage } from "../../utils";
import notify from "../../utils/notify";
import { FormAlert, PasswordField, SubmitButton } from "../auth/component/FormControls";

const MIN = 10; // same rule as the server (config/admin.php password_min)

const ChangePassword = () => {
  const [form, setForm] = useState({ current_password: "", password: "", password_confirmation: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (error) setError("");
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setDone("");
    if (!form.current_password) return setError("Enter your current password.");
    if (form.password.length < MIN) return setError(`The new password must be at least ${MIN} characters.`);
    if (form.password !== form.password_confirmation) return setError("The new passwords don't match.");
    if (form.password === form.current_password) return setError("The new password must be different from the current one.");

    setSaving(true);
    try {
      const res: any = await apiClient.post("account/password", form);
      const message = res?.data?.message ?? "Password changed.";
      setDone(message);
      notify.success(message);
      setForm({ current_password: "", password: "", password_confirmation: "" });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Admin | Change password">
      <PageHeader title="Change password" subtitle="Use this to replace a temporary password, or any time you want a new one." />

      <div className={`${card} max-w-xl p-6`}>
        <div className="mb-5 flex items-start gap-3 rounded-xl bg-hcBg px-4 py-3 text-sm text-honeConnectGray">
          <LuShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-honeConnectPurple" aria-hidden />
          <p>
            After the change you stay signed in here, and every other device is signed out. You'll get an email
            confirming the change.
          </p>
        </div>

        <FormAlert tone="error" message={error} />
        <FormAlert tone="success" message={done} />

        <form onSubmit={submit} noValidate className="space-y-5">
          <PasswordField
            id="current_password"
            label="Current password"
            autoComplete="current-password"
            value={form.current_password}
            onChange={set("current_password")}
          />
          <PasswordField
            id="password"
            label="New password"
            autoComplete="new-password"
            hint={`At least ${MIN} characters.`}
            value={form.password}
            onChange={set("password")}
          />
          <PasswordField
            id="password_confirmation"
            label="Confirm new password"
            autoComplete="new-password"
            value={form.password_confirmation}
            onChange={set("password_confirmation")}
          />
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <div className="w-full sm:w-56">
              <SubmitButton loading={saving} loadingText="Changing password">
                Change password
              </SubmitButton>
            </div>
            <Link to={SITELINKS.ACCOUNT} className="text-sm font-medium text-honeConnectPurple hover:underline">
              Back to My account
            </Link>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ChangePassword;
